import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import type { EventLogEntry } from "@/types/eventTypes";

/**
 * EventLogger - Fire-and-forget event logging for user actions
 *
 * Logs user actions to the event_logs table for audit trail,
 * future undo functionality, and AI feedback.
 *
 * Usage:
 *   eventLogger.log({
 *     event_type: "building.name.updated",
 *     entity_type: "building",
 *     entity_id: "123",
 *     field_name: "name",
 *     previous_value: "Old Name",
 *     new_value: "New Name",
 *   });
 */
/**
 * Rows per `event_logs` insert. One request carries the whole batch; this only
 * keeps a folder trash holding thousands of documents from posting a single
 * multi-megabyte body through the gateway.
 */
const INSERT_CHUNK_SIZE = 500;

/** An omitted field is dropped from the JSON body; these columns want a null. */
function orNull<T>(value: T | undefined): T | null {
  return value === undefined ? null : value;
}

function eventRow(entry: EventLogEntry, userId: number) {
  return {
    event_type: entry.event_type,
    user_id: userId,
    org_id: entry.org_id,
    entity_type: entry.entity_type,
    entity_id: entry.entity_id,
    field_name: orNull(entry.field_name),
    previous_value: orNull(entry.previous_value),
    new_value: orNull(entry.new_value),
    metadata: orNull(entry.metadata) ?? {},
  };
}

class EventLogger {
  private static instance: EventLogger;

  private constructor() {}

  static getInstance(): EventLogger {
    if (!EventLogger.instance) {
      EventLogger.instance = new EventLogger();
    }
    return EventLogger.instance;
  }

  /**
   * Log an event. Fire-and-forget - never throws, never blocks.
   */
  async log(entry: EventLogEntry): Promise<void> {
    return this.logMany([entry]);
  }

  /**
   * Log many events in one pass. Never throws, for the same reason `log` does
   * not.
   *
   * The actor is resolved once for the whole batch and the rows go in a single
   * insert: a folder trash cascades over every document beneath it, and one
   * `log()` per row is three round-trips each (`auth.getUser()`, the
   * `user_profiles` lookup, the insert) fired in the same tick — enough to hit
   * the browser's connection limit, and the user lookup is identical every
   * time.
   */
  async logMany(entries: EventLogEntry[]): Promise<void> {
    try {
      const valid = entries.filter((entry) => entry.org_id && entry.org_id.trim() !== "");
      if (valid.length < entries.length) {
        console.warn("EventLogger: Invalid org_id, skipping event log");
      }
      if (valid.length === 0) return;

      const userId = await this.currentProfileId();
      if (userId === null) return;

      // Insert event logs - org_id comes from caller
      const rows = valid.map((entry) => eventRow(entry, userId));

      for (let start = 0; start < rows.length; start += INSERT_CHUNK_SIZE) {
        const chunk = rows.slice(start, start + INSERT_CHUNK_SIZE);
        const { error } = await supabase.from("event_logs").insert(chunk);

        if (error) {
          // These rows are the source of truth for Documents → History, so a
          // rejected insert (RLS, a network drop mid folder-cascade, an
          // oversized body) is a permanent hole in the timeline: documents
          // sitting in the Trash that history claims were never deleted. Report
          // which slice was lost — later chunks still go, deliberately, so a
          // partial write must say what is missing or the hole is
          // undiagnosable in production.
          this.report(`EventLogger: Failed to log ${chunk.length} events`, error, {
            failedRange: [start, start + chunk.length - 1],
            totalRows: rows.length,
            eventTypes: [...new Set(chunk.map((row) => row.event_type))],
            entityIds: chunk.map((row) => row.entity_id),
          });
        }
      }
    } catch (error) {
      // Never throw - logging should not break the app
      this.report("EventLogger: Error logging event", error, { rows: entries.length });
    }
  }

  /**
   * The numeric `user_profiles.id` every row is written as, or null when there
   * is nobody to attribute the events to — an anonymous session has nothing to
   * log, so the batch is dropped rather than failing the action behind it.
   */
  private async currentProfileId(): Promise<number | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      console.warn("EventLogger: No authenticated user, skipping event log");
      return null;
    }

    const { data: profile } = await supabase
      .from("user_profiles")
      .select("id")
      .eq("auth_user_id", user.id)
      .single();

    if (!profile?.id) {
      console.warn("EventLogger: No user profile found, skipping event log");
      return null;
    }
    return profile.id;
  }

  /** Console for the developer, Sentry so the hole is visible in production. */
  private report(message: string, cause: unknown, extra: Record<string, unknown>): void {
    // Literal first argument: a caller-built format string here is what
    // `unsafe-formatstring` flags, and the message is more useful as data.
    console.error("EventLogger: event log write failed", { message, cause, ...extra });
    Sentry.captureException(cause instanceof Error ? cause : new Error(message), {
      level: "error",
      // Generic on purpose: `event_logs` backs every audit trail, not just
      // Documents → History. The lost event types are in `extra`.
      tags: { feature: "event-log" },
      extra: { ...extra, cause: cause instanceof Error ? undefined : cause },
    });
  }
}

export const eventLogger = EventLogger.getInstance();
